import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { supabase } from '@/lib/supabase-client';
import RecruiterOnboardingModal from '@/components/RecruiterOnboardingModal';
import { Building2 } from 'lucide-react';

export default function RecruiterSignUpPage() {
  const navigate = useNavigate();
  const { signUp } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingLoading, setOnboardingLoading] = useState(false);
  const [userId, setUserId] = useState('');
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [needsEmailConfirmation, setNeedsEmailConfirmation] = useState(false);

  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    organizationName: '',
    organizationDescription: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      console.log('[RecruiterSignUp] Submitting form with:', formData.email);
      const { data, error: signUpError } = await signUp(formData.email, formData.password, {
        full_name: formData.fullName,
        is_recruiter: true,
        organization_name: formData.organizationName,
        organization_description: formData.organizationDescription,
      });

      console.log('[RecruiterSignUp] Result:', {
        hasData: !!data,
        hasUser: !!data?.user,
        userId: data?.user?.id,
        error: signUpError?.message
      });

      if (signUpError) {
        console.error('[RecruiterSignUp] Error:', signUpError);
        throw signUpError;
      }

      if (data?.user) {
        console.log('[RecruiterSignUp] User created successfully');
        setUserId(data.user.id);
        setUserEmail(formData.email);

        if (!data.session) {
          console.log('[RecruiterSignUp] No session - email confirmation will be required after onboarding');
          setNeedsEmailConfirmation(true);
        }

        setShowOnboarding(true);
      } else {
        console.error('[RecruiterSignUp] No user in response data');
        setError('Failed to create user account');
      }
    } catch (err: any) {
      console.error('[RecruiterSignUp] Exception:', err);
      setError(err.message || 'An error occurred during sign up');
    } finally {
      setLoading(false);
    }
  };

  const handleOnboardingComplete = async (onboardingData: any) => {
    setOnboardingLoading(true);
    try {
      let organizationLogoUrl = null;

      if (!needsEmailConfirmation && onboardingData.organizationLogo) {
        const { uploadFile } = await import('@/lib/edge-functions');
        try {
          const logoData = await uploadFile(onboardingData.organizationLogo, 'organization-logos', userId);
          organizationLogoUrl = logoData.path;
        } catch (error) {
          console.error('Logo upload failed:', error);
        }
      }

      const updateData: any = {
        updated_at: new Date().toISOString(),
      };

      const { error: profileError } = await supabase
        .from('user_profiles')
        .update(updateData)
        .eq('id', userId);

      if (profileError) {
        console.error('Profile update error:', profileError);
        setError('Failed to save onboarding data');
        setOnboardingLoading(false);
        return;
      }

      if (organizationLogoUrl) {
        const { data: memberData } = await supabase
          .from('organization_members')
          .select('organization_id')
          .eq('user_id', userId)
          .maybeSingle();

        if (memberData?.organization_id) {
          await supabase
            .from('organizations')
            .update({ logo_url: organizationLogoUrl })
            .eq('id', memberData.organization_id);
        }
      }

      setOnboardingLoading(false);
      setShowOnboarding(false);

      if (needsEmailConfirmation) {
        console.log('[RecruiterSignUp] Onboarding complete, showing email confirmation screen');
        setSignupSuccess(true);
      } else {
        console.log('[RecruiterSignUp] Onboarding complete, redirecting to dashboard');
        navigate('/dashboard');
      }
    } catch (err: any) {
      console.error('Error saving onboarding:', err);
      setError(err.message || 'Failed to complete onboarding');
      setOnboardingLoading(false);
    }
  };

  const handleSkipOnboarding = () => {
    if (needsEmailConfirmation) {
      setShowOnboarding(false);
      setSignupSuccess(true);
    } else {
      navigate('/dashboard');
    }
  };

  if (signupSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <h2 className="text-2xl font-semibold text-slate-900 mb-2">Check your email</h2>
          <p className="text-slate-600 mb-6">
            We've sent a confirmation link to <strong>{userEmail}</strong>
          </p>
          <p className="text-sm text-slate-500 mb-6">
            Click the link in the email to verify your account. The email is from <strong>Teech Interviews</strong> (via Supabase).
          </p>
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-left text-sm text-slate-600">
            <p className="font-medium text-slate-700 mb-2">After confirming:</p>
            <ol className="list-decimal list-inside space-y-1 ml-2">
              <li>Click the confirmation link in your email</li>
              <li>You'll be redirected back to this site</li>
              <li>Sign in with your email and password</li>
              <li>Access your recruiter dashboard</li>
            </ol>
          </div>
          <a
            href="/login"
            className="mt-6 inline-block text-sm text-blue-600 hover:text-blue-700 font-medium"
          >
            Go to sign in page
          </a>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen grid md:grid-cols-2">
        <div className="flex flex-col justify-between px-6 py-8 md:px-16 md:py-10 bg-white">
          <div className="flex items-center justify-center">
            <img
              src="https://suqzclerofxnjwpmnbdy.supabase.co/storage/v1/object/public/Site%20Images/png%20logo.png"
              alt="Interview Trainer"
              className="h-20 w-auto"
            />
          </div>

          <div className="max-w-md mx-auto w-full mt-10 mb-12">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-semibold text-slate-900">
                  Recruiter Sign Up
                </h1>
              </div>
            </div>
            <p className="text-slate-500 mb-8">
              Create your organization account to post jobs and find top talent.
            </p>

            {error && (
              <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-600">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Full name
                </label>
                <input
                  type="text"
                  required
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  className="w-full rounded-full border border-slate-200 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500/70 focus:border-blue-500 bg-slate-50"
                  placeholder="Jane Smith"
                  disabled={loading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Email address
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full rounded-full border border-slate-200 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500/70 focus:border-blue-500 bg-slate-50"
                  placeholder="you@company.com"
                  disabled={loading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Password
                </label>
                <input
                  type="password"
                  required
                  minLength={6}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full rounded-full border border-slate-200 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500/70 focus:border-blue-500 bg-slate-50"
                  placeholder="Create a secure password"
                  disabled={loading}
                />
              </div>

              <div className="pt-2">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Organization name
                </label>
                <input
                  type="text"
                  required
                  value={formData.organizationName}
                  onChange={(e) => setFormData({ ...formData, organizationName: e.target.value })}
                  className="w-full rounded-full border border-slate-200 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500/70 focus:border-blue-500 bg-slate-50"
                  placeholder="Acme Corporation"
                  disabled={loading}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Organization description (optional)
                </label>
                <textarea
                  rows={3}
                  value={formData.organizationDescription}
                  onChange={(e) => setFormData({ ...formData, organizationDescription: e.target.value })}
                  className="w-full rounded-2xl border border-slate-200 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500/70 focus:border-blue-500 bg-slate-50"
                  placeholder="Brief description of your organization"
                  disabled={loading}
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full mt-4 rounded-full bg-blue-600 text-white py-2.5 text-sm font-medium shadow-sm hover:bg-blue-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Creating account...' : 'Create recruiter account'}
              </button>
            </form>

            <p className="mt-6 text-xs text-slate-400">
              By signing up you agree to our Terms and Privacy Policy.
            </p>
          </div>

          <div className="space-y-2 text-xs text-slate-400 text-center md:text-left">
            <p>
              Already have an account?{' '}
              <a href="/login" className="text-blue-600 hover:underline">
                Sign in
              </a>
            </p>
            <p>
              Are you a job seeker?{' '}
              <a href="/signup" className="text-blue-600 hover:underline">
                Sign up as a candidate
              </a>
            </p>
          </div>
        </div>

        <div className="hidden md:block relative">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-blue-500 to-cyan-400 opacity-90"></div>
          <div className="absolute inset-8 rounded-3xl border border-white/40 backdrop-blur-sm bg-white/10 flex flex-col justify-between p-8">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-white/70 mb-4">
                For Hiring Teams
              </p>
              <h2 className="text-3xl font-semibold text-white mb-3">
                Find the right talent faster
              </h2>
              <p className="text-sm text-white/80 max-w-sm mb-6">
                Post job openings, review AI-scored candidate interviews, and streamline your hiring process.
              </p>

              <div className="rounded-2xl overflow-hidden shadow-2xl border-2 border-white/30">
                <img
                  src="https://suqzclerofxnjwpmnbdy.supabase.co/storage/v1/object/public/Site%20Images/Interview%20page.jpg"
                  alt="Recruiter dashboard preview"
                  className="w-full h-auto"
                />
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-start gap-3 text-white/90">
                <div className="w-1.5 h-1.5 rounded-full bg-white/80 mt-2 flex-shrink-0"></div>
                <span className="text-sm">Post jobs and manage candidates in one place</span>
              </div>
              <div className="flex items-start gap-3 text-white/90">
                <div className="w-1.5 h-1.5 rounded-full bg-white/80 mt-2 flex-shrink-0"></div>
                <span className="text-sm">AI-scored interviews with detailed analytics</span>
              </div>
              <div className="flex items-start gap-3 text-white/90">
                <div className="w-1.5 h-1.5 rounded-full bg-white/80 mt-2 flex-shrink-0"></div>
                <span className="text-sm">Save time with automated candidate screening</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {showOnboarding && (
        <>
          <RecruiterOnboardingModal
            isOpen={showOnboarding}
            onComplete={handleOnboardingComplete}
            onSkip={handleSkipOnboarding}
            needsEmailConfirmation={needsEmailConfirmation}
          />
          {onboardingLoading && (
            <div className="fixed inset-0 bg-slate-900/80 flex items-center justify-center z-[60]">
              <div className="bg-white rounded-2xl p-8 max-w-sm mx-4 text-center shadow-2xl">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Setting up your organization...</h3>
                <p className="text-sm text-slate-600">Uploading your files and saving your preferences</p>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
}
